//import '@glidejs/glide/src/assets/sass/glide.core.scss';
//import '@glidejs/glide/src/assets/sass/glide.theme.scss';
import Glide from '@glidejs/glide';
// import Glide from './glide.esm';
import BaseComponent from './BaseComponent';

export default class BaseCarousel extends BaseComponent {
  get BASE_CONF() {
    return {
      /**
       * Type of the movement.
       *
       * Available types:
       * `slider` - Rewinds slider to the start/end when it reaches the first or last slide.
       * `carousel` - Changes slides without starting over when it reaches the first or last slide.
       *
       * @type {String}
       */
      type: 'slider',

      /**
       * Start at specific slide number defined with zero-based index.
       *
       * @type {Number}
       */
      startAt: 0,

      /**
       * A number of slides visible on the single viewport.
       *
       * @type {Number}
       */
      perView: 1,

      /**
       * Focus currently active slide at a specified position in the track.
       *
       * Available inputs:
       * `center` - Current slide will be always focused at the center of a track.
       * `0,1,2,3...` - Current slide will be focused on the specified zero-based index.
       *
       * @type {String|Number}
       */
      focusAt: 0,

      /**
       * A size of the gap added between slides.
       *
       * @type {Number}
       */
      gap: 10,

      /**
       * Change slides after a specified interval. Use `false` for turning off autoplay.
       *
       * @type {Number|Boolean}
       */
      autoplay: false,

      /**
       * Stop autoplay on mouseover event.
       *
       * @type {Boolean}
       */
      hoverpause: true,

      /**
       * Allow for changing slides with left and right keyboard arrows.
       *
       * @type {Boolean}
       */
      keyboard: true,

      /**
       * Stop running `perView` number of slides from the end. Use this
       * option if you don't want to have an empty space after
       * a slider. Works only with `slider` type and a
       * non-centered `focusAt` setting.
       *
       * @type {Boolean}
       */
      bound: false,

      /**
       * Minimal swipe distance needed to change the slide. Use `false` for turning off a swiping.
       *
       * @type {Number|Boolean}
       */
      swipeThreshold: 15,

      /**
       * Minimal mouse drag distance needed to change the slide. Use `false` for turning off a dragging.
       *
       * @type {Number|Boolean}
       */
      dragThreshold: 10,

      /**
       * A maximum number of slides to which movement will be made on swiping or dragging. Use `false` for unlimited.
       *
       * @type {Number|Boolean}
       */
      perTouch: false,

      /**
       * Moving distance ratio of the slides on a swiping and dragging.
       *
       * @type {Number}
       */
      touchRatio: 0.5,

      /**
       * Angle required to activate slides moving on swiping or dragging.
       *
       * @type {Number}
       */
      touchAngle: 45,

      /**
       * Duration of the animation in milliseconds.
       *
       * @type {Number}
       */
      animationDuration: 600,

      /**
       * Allows looping the `slider` type. Slider will rewind to the first/last slide when it's at the start/end.
       *
       * @type {Boolean}
       */
      rewind: false,

      /**
       * Duration of the rewinding animation of the `slider` type in milliseconds.
       *
       * @type {Number}
       */
      rewindDuration: 1000,

      /**
       * Easing function for the animation.
       *
       * @type {String}
       */
      animationTimingFunc: 'cubic-bezier(.165, .840, .440, 1)',

      /**
       * Throttle costly events at most once per every wait milliseconds.
       *
       * @type {Number}
       */
      throttle: 10,

      /**
       * Moving direction mode.
       *
       * Available inputs:
       * - 'ltr' - left to right movement,
       * - 'rtl' - right to left movement.
       *
       * @type {String}
       */
      direction: 'ltr',

      /**
       * The distance value of the next and previous viewports which
       * have to peek in the current view. Accepts number and
       * pixels as a string. Left and right peeking can be
       * set up separately with a directions object.
       *
       * For example:
       * `100` - Peek 100px on the both sides.
       * { before: 100, after: 50 }` - Peek 100px on the left side and 50px on the right side.
       *
       * @type {Number|String|Object}
       */
      peek: 0,

      /**
       * Collection of options applied at specified media breakpoints.
       * For example: display two slides per view under 800px.
       * `{
       *   '800px': {
       *     perView: 2
       *   }
       * }`
       */
      breakpoints: {},

      /**
       * Collection of internally used HTML classes.
       *
       * @todo Refactor `slider` and `carousel` properties to single `type: { slider: '', carousel: '' }` object
       * @type {Object}
       */
      classes: {
        direction: {
          ltr: 'glide--ltr',
          rtl: 'glide--rtl'
        },
        slider: 'glide--slider',
        carousel: 'glide--carousel',
        swipeable: 'glide--swipeable',
        dragging: 'glide--dragging',
        cloneSlide: 'glide__slide--clone',
        activeNav: 'glide__bullet--active',
        activeSlide: 'glide__slide--active',
        disabledArrow: 'glide__arrow--disabled'
      }
    };
  }

  constructor(element) {
    super(element);
  }

  mount(element, options) {
    let self = this;
    const conf = { ...this.BASE_CONF, ...options };
    let glide = new Glide(element, conf);

    glide.on('build.after', () => {
      this.updateArrows(glide);
      this.updateSlides(glide);
      // this.updateTrackHeight(glide); PITTI20-128

      glide.selector.dispatchEvent(new CustomEvent('glide.build.after', {
        detail: {
          'glide': glide
        }
      }));
    });

    glide.on('run.after', () => {
      this.updateArrows(glide);
      this.updateSlides(glide);
      // this.updateTrackHeight(glide); PITTI20-128
    });

    glide.mount();
  }

  // https://github.com/glidejs/glide/issues/307
  updateArrows(glide) {
    glide.selector.querySelectorAll('.glide__arrow').forEach((item, index) => {
      switch (index) {
        case 0:
          item.style.display = (glide.index > 0) ? '': 'none';
          break;
        case 1:
          const length = glide.selector.querySelectorAll('.glide__slide').length;
          item.style.display = (glide.index < length - parseInt(glide.settings.perView)) ? '': 'none';
          break;
      }
    });
  }

  // https://github.com/glidejs/glide/issues/303
  updateSlides(glide) {
    Array.from(glide.selector.querySelectorAll('.glide__slide')).forEach((item, index) => {
      const visible = glide.index <= index && index < (glide.index + parseInt(glide.settings.perView));
      item.classList[visible ? 'add' : 'remove']('glide__slide--visible');
    })
  }

  updateTrackHeight(glide) {
    var glideTrack = glide.selector.querySelector('.glide__track');
    var trackHeight = this.outerHeight(glideTrack);
    var slideHeight = this.outerHeight(glide.selector.querySelector('.glide__slide--active'));
    if (slideHeight !== glideTrack) {
      glideTrack.style.height = slideHeight;
    }
  }

  outerHeight(el) {
    if (!el) return;
    var height = el.offsetHeight;
    var style = window.getComputedStyle(el);

    height += parseInt(style.marginTop, 10) + parseInt(style.marginBottom, 10);
    return height;
  }
}
